import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SMBeamModel, SectionMaterialStrength, LimitStateSelection, CombinationResultData } from '../types'
import { ResizablePanels } from '@structura/ui'
import InfoBanner from '../components/InfoBanner'
import { useResizablePanels } from '@structura/ui'
import { useTranslation, TranslationKey } from '@structura/i18n'

// Leading load category → its translation key (None → "no variable leading").
const CATEGORY_LABEL_KEY: Record<string, TranslationKey> = {
  Variable: 'beam.results.catVariable',
  Snow: 'beam.results.catSnow',
  Wind: 'beam.results.catWind',
  Permanent: 'beam.results.catPermanent',
  Envelope: 'beam.results.catEnvelope',
  None: 'beam.results.noVariableLeading'
}
import ResultsControlPanel from './results/ResultsControlPanel'
import ResultsVisualizationPanel from './results/ResultsVisualizationPanel'

interface ResultsTabProps {
  // Base model
  model: SMBeamModel
  // Analysis result & diagram state
  result: any
  selectedDiagram: any
  setSelectedDiagram: (diagram: any) => void
  showValues: boolean
  setShowValues: (value: boolean) => void
  showMaxMin: boolean
  setShowMaxMin: (value: boolean) => void
  valueDecimals: 2 | 4
  setValueDecimals: (value: 2 | 4) => void
  diagramScale: number
  setDiagramScale: (scale: number) => void
  valueDensity: number
  setValueDensity: (density: number) => void
  valueFontSize: number
  setValueFontSize: (size: number) => void
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  sectionMaterialStrength?: SectionMaterialStrength | null
  // Analysis + combinations
  onAnalyzeAll: () => Promise<void>
  analyzingAll: boolean
  onAnalyzeTimoshenko?: () => Promise<void>
  analyzingTimoshenko?: boolean
  analysisTheory?: 'EulerBernoulli' | 'Timoshenko'
  selectedLimitState: LimitStateSelection
  setSelectedLimitState: (next: LimitStateSelection) => void
  selectedCombinationId: string
  setSelectedCombinationId: (next: string) => void
  lastAnalysisSummary?: string[] | null
  
  // Advanced Analysis
  calculateSecondOrderEffects?: (options?: { combinationId?: string }) => Promise<void>
  calculatingSecondOrderEffects?: boolean
  secondOrderEffectsResult?: any
  calculateDynamics?: () => Promise<void>
  calculatingDynamics?: boolean
  dynamicsResult?: any
}

const ResultsTab: React.FC<ResultsTabProps> = ({ 
  model,
  result,
  selectedDiagram,
  setSelectedDiagram,
  showValues,
  setShowValues,
  showMaxMin,
  setShowMaxMin,
  valueDecimals,
  setValueDecimals,
  diagramScale,
  setDiagramScale,
  valueDensity,
  setValueDensity,
  valueFontSize,
  setValueFontSize,
  currentMaterial,
  timberKind,
  onAnalyzeAll,
  analyzingAll,
  onAnalyzeTimoshenko,
  analyzingTimoshenko,
  analysisTheory,
  sectionMaterialStrength,
  selectedLimitState,
  setSelectedLimitState,
  selectedCombinationId,
  setSelectedCombinationId,
  lastAnalysisSummary,
  calculateSecondOrderEffects,
  calculatingSecondOrderEffects,
  secondOrderEffectsResult,
  calculateDynamics,
  calculatingDynamics,
  dynamicsResult
}) => {
  const { t } = useTranslation()
  const { leftWidth, rightWidth, handleMouseDown, isDragging, containerRef } = useResizablePanels({
    defaultLeftWidth: 30,
    minLeftWidth: 3,
    maxLeftWidth: 97,
    storageKey: 'beamDesigner_resultsTab_leftWidth'
  })

  // Translated label for a leading load category; unknown keys fall back to the raw key.
  const categoryLabel = useCallback((key: string): string => {
    const translationKey = CATEGORY_LABEL_KEY[key]
    return translationKey ? t(translationKey) : key
  }, [t])

  // Compute slenderness ratio L/h (longest span / section height in meters)
  const slendernessRatio = useMemo(() => {
    const heightM = (model.crossSection?.height ?? 0) / 1000
    if (heightM <= 0 || model.spans.length === 0) return undefined
    const maxSpanLength = Math.max(...model.spans.map(s => s.length))
    return maxSpanLength / heightM
  }, [model.spans, model.crossSection?.height])


  const envelopeResults = React.useMemo(() => {
    if (!result)
    {
      return undefined
    }
    return (result as any).Results || (result as any).results
  }, [result])

  const combinationResults = React.useMemo(() => {
    if (!result)
    {
      return [] as CombinationResultData[]
    }
    return ((result as any).CombinationResults || (result as any).combinationResults || []) as CombinationResultData[]
  }, [result])

  const activeResults = React.useMemo(() => {
    if (!result) return envelopeResults
    if (selectedCombinationId === 'envelope' || combinationResults.length === 0) {
      return envelopeResults
    }
    const combo = combinationResults.find(c => c.combinationId === selectedCombinationId)
    return combo?.results ?? envelopeResults
  }, [envelopeResults, combinationResults, selectedCombinationId, result])

  const activeCombination = React.useMemo(() => {
    if (selectedCombinationId === 'envelope') {
      return undefined
    }
    return combinationResults.find(combo => combo.combinationId === selectedCombinationId)
  }, [combinationResults, selectedCombinationId])

  useEffect(() => {
    if (selectedCombinationId !== 'envelope') {
      const exists = combinationResults.some(combo => combo.combinationId === selectedCombinationId)
      if (!exists && combinationResults.length > 0) {
        setSelectedCombinationId(combinationResults[0].combinationId)
      }
    } else if (combinationResults.length > 0) {
      // Default to first combination if currently set to 'envelope'
      setSelectedCombinationId(combinationResults[0].combinationId)
    }
  }, [combinationResults, selectedCombinationId, setSelectedCombinationId])

  const combinationOptions = React.useMemo(() => {
    const options: Array<{ id: string; label: string }> = []
    for (const combo of combinationResults)
    {
      const leadingKey = combo.leadingCategory ?? 'None'
      const label = leadingKey === 'Envelope' || leadingKey === 'None'
        ? categoryLabel(leadingKey)
        : t('beam.results.leading', { category: categoryLabel(leadingKey) })
      options.push({ id: combo.combinationId, label })
    }
    return options
  }, [combinationResults, categoryLabel, t])

  const loadFactorRows = React.useMemo(() => {
    if (!activeCombination || !activeCombination.loadFactors || activeCombination.loadFactors.length === 0)
    {
      return null
    }
    return activeCombination.loadFactors.map(factor => ({
      key: `${activeCombination.combinationId}-${factor.category}`,
      categoryLabel: categoryLabel(factor.category),
      isLeading: factor.isLeading,
      gamma: factor.gamma,
      psi: factor.psi,
      factor: factor.factor
    }))
  }, [activeCombination, categoryLabel])

  const activeDiagramSet = activeResults?.diagramData
  const activeSupportReactions = activeResults?.supportReactions

  const displayedLimitState = activeResults?.limitState || selectedLimitState
  const displayedLeadingCategory = activeResults?.leadingCategory ?? 'None'
  const leadingBadgeText = displayedLeadingCategory === 'None'
    ? categoryLabel('None')
    : t('beam.results.leading', { category: categoryLabel(displayedLeadingCategory) })

  const analysisSucceeded = Boolean(result && ((result as any).Success ?? (result as any).success))
  const analysisFailed = Boolean(result && !analysisSucceeded)

  const [bannerDismissed, setBannerDismissed] = useState(false)

  useEffect(() => {
    // Reset dismissal when a new analysis summary arrives
    setBannerDismissed(false)
  }, [lastAnalysisSummary])

  // Persist UI settings per-diagram type
  useEffect(() => {
    try {
      const key = `beamDiagram_ui_${selectedDiagram}`
      const payload = {
        scale: diagramScale,
        valueDensity,
        valueFontSize,
        showValues,
        showMaxMin,
        valueDecimals
      }
      localStorage.setItem(key, JSON.stringify(payload))
    } catch { /* localStorage unavailable */ }
  }, [selectedDiagram, diagramScale, valueDensity, valueFontSize, showValues, showMaxMin, valueDecimals])

  // Load UI settings when diagram type changes
  useEffect(() => {
    try {
      const key = `beamDiagram_ui_${selectedDiagram}`
      const saved = localStorage.getItem(key)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (typeof parsed.scale === 'number') setDiagramScale(parsed.scale)
        if (typeof parsed.valueDensity === 'number') setValueDensity(parsed.valueDensity)
        if (typeof parsed.valueFontSize === 'number') setValueFontSize(parsed.valueFontSize)
        if (typeof parsed.showValues === 'boolean') setShowValues(parsed.showValues)
        if (typeof parsed.showMaxMin === 'boolean') setShowMaxMin(parsed.showMaxMin)
        if (parsed.valueDecimals === 2 || parsed.valueDecimals === 4) setValueDecimals(parsed.valueDecimals)
      }
    } catch { /* localStorage unavailable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDiagram])

  return (
    <>
      <ResizablePanels
        leftPanel={(
          <ResultsControlPanel
            hasResult={Boolean(result)}
            analysisSucceeded={analysisSucceeded}
            analysisFailed={analysisFailed}
            analysisErrorDetail={analysisFailed ? ((result as any).Error ?? (result as any).error ?? (result as any).message ?? undefined) : undefined}
            onAnalyzeAll={onAnalyzeAll}
            analyzingAll={analyzingAll}
            onAnalyzeTimoshenko={onAnalyzeTimoshenko}
            analyzingTimoshenko={analyzingTimoshenko}
            analysisTheory={analysisTheory}
            selectedLimitState={selectedLimitState}
            setSelectedLimitState={setSelectedLimitState}
            selectedCombinationId={selectedCombinationId}
            setSelectedCombinationId={setSelectedCombinationId}
            combinationOptions={combinationOptions}
            loadFactors={loadFactorRows}
            showEnvelopeNotice={combinationOptions.length > 1}
            selectedDiagram={selectedDiagram}
            setSelectedDiagram={setSelectedDiagram}
            showValues={showValues}
            setShowValues={setShowValues}
            showMaxMin={showMaxMin}
            setShowMaxMin={setShowMaxMin}
            valueDecimals={valueDecimals}
            setValueDecimals={setValueDecimals}
            diagramScale={diagramScale}
            setDiagramScale={setDiagramScale}
            valueDensity={valueDensity}
            setValueDensity={setValueDensity}
            valueFontSize={valueFontSize}
            setValueFontSize={setValueFontSize}
            calculateSecondOrderEffects={calculateSecondOrderEffects}
            calculatingSecondOrderEffects={calculatingSecondOrderEffects}
            secondOrderEffectsResult={secondOrderEffectsResult}
            calculateDynamics={calculateDynamics}
            calculatingDynamics={calculatingDynamics}
            dynamicsResult={dynamicsResult}
            currentMaterial={currentMaterial}
            slendernessRatio={slendernessRatio}
          />
        )}
        rightPanel={(
          <div>
            <ResultsVisualizationPanel
              model={model}
              currentMaterial={currentMaterial}
          timberKind={timberKind}
              sectionMaterialStrength={sectionMaterialStrength}
              analysisSucceeded={analysisSucceeded}
              selectedDiagram={selectedDiagram}
              diagramScale={diagramScale}
              showValues={showValues}
              showMaxMin={showMaxMin}
              valueDecimals={valueDecimals}
              valueDensity={valueDensity}
              valueFontSize={valueFontSize}
              activeDiagramSet={activeDiagramSet}
              activeSupportReactions={activeSupportReactions}
              displayedLimitState={displayedLimitState}
              displayedLeadingCategory={displayedLeadingCategory}
              leadingBadgeText={leadingBadgeText}
            />
            {lastAnalysisSummary && analysisSucceeded && !bannerDismissed && (
              <div className="mt-3">
                <InfoBanner
                  title={t('beam.results.multiLimitState')}
                  lines={lastAnalysisSummary}
                  variant="success"
                  onClose={() => setBannerDismissed(true)}
                />
              </div>
            )}
          </div>
        )}
        leftWidth={leftWidth}
        rightWidth={rightWidth}
        onMouseDown={handleMouseDown}
        isDragging={isDragging}
        containerRef={containerRef}
      />
    </>
  )
}

export default ResultsTab
